import { useTranslation } from "react-i18next";
import { Divider, Form, Icon } from "semantic-ui-react";

import { BrandGroupSelect, Can } from "components";
import { Element } from "components/ReactScroll/index";
import ENV from "config/Env";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, BARCODE_TYPES, ROLES } from "models";
import { CompanyStampUpload } from "modules/DealerLocationDetails/components/General/components";
import { ASTERISK_PLACEHOLDER } from "modules/DealerOverviewDashboard/components";
import { useLanguagesQuery } from "modules/DealersDashboard/hooks";
import { areIpAddressesValid, getInvalidIpAddressLine } from "modules/LocationOverviewDashboard/utils";
import { copyToClipboard, countryDropdownOptions, isAdmin } from "utils/common";

import type { Dealer, DealerLocation } from "models";
import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

const BAR_CODE_TYPE_NUMBERS = new Map([
  [BARCODE_TYPES.Barcode128, 128],
  [BARCODE_TYPES.Barcode93, 93],
  [BARCODE_TYPES.Barcode39, 39],
  [BARCODE_TYPES.Barcode25, 25],
  [BARCODE_TYPES.Barcode11, 11]
]);

type GeneralProps = {
  tabId: string;
  location: DealerLocation;
  dealer?: Dealer;
};

export const General = ({ tabId, location, dealer }: GeneralProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { data: languageOptions = [] } = useLanguagesQuery();

  const { watch, setValue, formState } = FormHelper.useFormContext();
  const [name, commercialName, country, languageCode, brandGroupIds, ipAddressEnabled, ipAddresses] = watch([
    "name",
    "commercial_name",
    "country",
    "language_code",
    "brand_group_ids",
    "ip_address_enabled",
    "ip_addresses"
  ]);

  const isAdminUser = Boolean(user && isAdmin(user.role_id));
  const isNameDisabled = !user || user.role_id > ROLES.SupportAdmin;
  const hasIpAddressError = formState.isSubmitted && ipAddressEnabled && !areIpAddressesValid(ipAddresses);
  const invalidIpAddressLine = getInvalidIpAddressLine(ipAddresses);
  const barCodeTypeOptions = [...BAR_CODE_TYPE_NUMBERS].map(([value, barCodeNumber]) => ({
    key: value,
    value,
    text: `${t("v8_bar_code", "Bar code")} ${barCodeNumber}`
  }));

  return (
    <Element name={tabId} className="dealer-location-section">
      <div className="section-title">
        <div>
          <Icon className="gears" />
        </div>
        <span className="section-title-text">{t("v8_general", "General")}</span>
      </div>

      <div className="dealer-location-subheading">{t("v8_location_info", "Location Info")}</div>
      <Form.Group>
        <Form.Field width={16}>
          <FormHelper.Field.Checkbox name="active" label={t("v8_active", "Active")} />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={8} required error={formState.isSubmitted && !name}>
          <label>{t("v8_location_name", "Location Name")}</label>
          <FormHelper.Field.Input name="name" inputProps={{ disabled: isNameDisabled }} />
        </Form.Field>
        <Form.Field width={8} required error={formState.isSubmitted && !commercialName}>
          <label>{t("v8_commercial_name", "Commercial name")}</label>
          <FormHelper.Field.Input name="commercial_name" />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={8}>
          <label>{t("v8_location_header", "Location header")}</label>
          <FormHelper.Field.Input name="headline" />
        </Form.Field>
        <Form.Field width={8}>
          <label>{t("v8_location_footer", "Location footer")}</label>
          <FormHelper.Field.Input name="footerline" />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={8} required error={formState.isSubmitted && !country}>
          <label>{t("v8_country", "Country")}</label>
          <FormHelper.Field.Dropdown name="country" options={countryDropdownOptions} dropdownProps={{ search: true }} />
        </Form.Field>
        <Form.Field width={8}>
          <label>{t("v8_city", "City")}</label>
          <FormHelper.Field.Input name="city" />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={8}>
          <label>{t("v8_street", "Street")}</label>
          <FormHelper.Field.Input name="street" />
        </Form.Field>
        <Form.Field width={8}>
          <label>{t("v8_postal_code", "Postal code")}</label>
          <FormHelper.Field.Input name="postal_code" />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={8}>
          <label>{t("v8_phone", "Phone")}</label>
          <FormHelper.Field.Input name="phone_nr" />
        </Form.Field>
        <Form.Field width={8} required error={formState.isSubmitted && !languageCode}>
          <label>{t("v8_language", "Language")}</label>
          <FormHelper.Field.Dropdown name="language_code" options={languageOptions} dropdownProps={{ search: true }} />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.Logos}>
          <Form.Field width={8}>
            <label>{t("v8_upload_logo", "Upload Logo")} (.jpg, .jpeg, .png)</label>
            <FormHelper.Field.UploadImage name="logo" path="/dealers/logo/upload" baseUrl={ENV.dealerBaseURL} fileFieldName="file" />
          </Form.Field>
        </Can>
        <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.Files}>
          <Form.Field width={8}>
            <label>{t("v8_upload_company_stamp", "Upload Company Stamp")} (.jpg, .jpeg, .png)</label>
            <CompanyStampUpload />
          </Form.Field>
        </Can>
      </Form.Group>

      {isAdminUser && (
        <>
          <Divider />
          <div className="dealer-location-subheading">{t("v8_brands", "Brands")}</div>
          <Form.Group>
            <Form.Field width={8}>
              <label>{t("v8_brand_groups", "Brand Groups")}</label>
              <BrandGroupSelect value={brandGroupIds ?? []} onChange={ids => setValue("brand_group_ids", ids)} />
            </Form.Field>
          </Form.Group>

          <div className="dealer-location-subheading">{t("v8_system_identifiers", "System Identifiers")}</div>
          <Form.Group>
            <Form.Field width={8}>
              <label>{t("v8_importer_version", "Importer Version")}</label>
              <FormHelper.Field.Input name="importer_version" />
            </Form.Field>
            <Form.Field width={8}>
              <label>{t("v8_bar_code_type", "Bar code type")}</label>
              <FormHelper.Field.Dropdown name="bar_code_type" options={barCodeTypeOptions} />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={8}>
              <label>{t("v8_location_key", "Location key")}</label>
              <div className="dealer-location-readonly-value">{location.id}</div>
            </Form.Field>
            <Form.Field width={8}>
              <label>{t("v8_datastore_key", "Datastore key")}</label>
              <div className="dealer-location-readonly-value">
                <Icon className="copy" title={t("v8_copy_to_clipboard", "Copy to clipboard")} onClick={() => copyToClipboard(location.datastore_key)} />
                <div>{location.datastore_key}</div>
              </div>
            </Form.Field>
          </Form.Group>
        </>
      )}

      <Divider />
      <div className="dealer-location-columns">
        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_ip_address", "IP Address")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox
              name="ip_address_enabled"
              label={t("v8_enable_ip_address", "Enable IP address")}
              onChange={enabled => !enabled && setValue("ip_addresses", "")}
            />
          </Form.Field>
          <Form.Field error={hasIpAddressError}>
            <label>{t("v8_ip_addresses", "IP addresses")}</label>
            <FormHelper.Field.TextArea
              name="ip_addresses"
              textAreaProps={{
                rows: 4,
                disabled: !ipAddressEnabled,
                placeholder: t("v8_one_ip_address_or_subnet_per_line", "One IP address or subnet per line")
              }}
            />
            {hasIpAddressError && !!invalidIpAddressLine && (
              <div className="dealer-location-field-error">
                {t("v8_line_{{line}}_is_not_a_valid_ip_address_or_subnet", "Line {{line}} is not a valid IP address or subnet", { line: invalidIpAddressLine })}
              </div>
            )}
          </Form.Field>
        </div>
        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_privacy", "Privacy")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_employee_name_visible" label={t("v8_show_employee_name", "Show employee name")} />
          </Form.Field>
          <div className="dealer-location-subheading">{t("v8_claire_v8", "Claire V8")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="redirect_to_new_web_version" label={t("v8_redirect_users_to_v8", "Redirect users to V8")} />
          </Form.Field>
          {isAdminUser && (
            <>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="convert_checklist_to_intervention_is_enabled"
                  label={t("v8_convert_checklist_item_to_intervention", "Convert checklist item to intervention")}
                />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox name="manually_create_intervention_is_enabled" label={t("v8_manually_create_intervention", "Manually create intervention")} />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox name="create_wo_is_enabled" label={t("v8_create_wo", "Create WO")} />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="can_edit_checklist_after_car_ready"
                  label={t("v8_checklist_item_editable_after_car_ready", "Checklist item editable after Car Ready")}
                />
              </Form.Field>
            </>
          )}
        </div>
      </div>

      {isAdminUser && dealer?.dbb_enabled && (
        <>
          <Divider />
          <div className="dealer-location-subheading">{t("v8_dbb", "DBB")}</div>
          <Form.Group>
            <Form.Field width={16}>
              <FormHelper.Field.Checkbox name="dbb_enabled" label={t("v8_dbb_enabled", "DBB Enabled")} />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={8}>
              <label>{t("v8_dbb_location_id", "DBB Location ID")}</label>
              <FormHelper.Field.Input name="dbb_location_id" />
            </Form.Field>
            <Form.Field width={8}>
              <label>{t("v8_dbb_user", "DBB User")}</label>
              <FormHelper.Field.Input name="dbb_user" preventAutofill />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={8}>
              <label>{t("v8_dbb_password", "DBB Password")}</label>
              <FormHelper.Field.Input
                name="dbb_password"
                preventAutofill
                inputProps={{ type: "password", placeholder: location.has_dbb_password ? ASTERISK_PLACEHOLDER : "" }}
              />
            </Form.Field>
          </Form.Group>
        </>
      )}
    </Element>
  );
};
